import { Observable, of, Subject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { WebSocketConnectionState, WebSocketServiceBase, WebSocketServiceConfig } from 'types';

export class WSStubResponse {
  constructor(public command: string, public data?: any, public error?: string | null) {}

  toJson() {
    return {
      command: this.command,
      result: this.error === null || this.error === undefined || this.error.length === 0,
      ...this.data,
      error: this.error,
    };
  }
}

type WSStubRequestSchemaParamType = [name: string, valueType: any | any[], required: boolean];
type WSStubRequestSchemaParamTypeSimple = [name: string, valueType: any[], required: boolean];
type WSStubRequestSchemaCallback = (data: any) => void;
type WSStubRequestSchemaResponse = WSStubResponse | ((data: any) => WSStubResponse);

export class WSStubRequestSchema {
  public callback: WSStubRequestSchemaCallback | undefined;
  public response: WSStubRequestSchemaResponse | undefined;
  private params: WSStubRequestSchemaParamTypeSimple[] = [];

  constructor(public command: string) {}

  withParams(params: WSStubRequestSchemaParamType[]): WSStubRequestSchema {
    this.params = params.map(([name, valueType, required]) => [
      name,
      valueType ? (Array.isArray(valueType) ? valueType : [valueType]) : [],
      required,
    ]);

    return this;
  }

  withCallback(callback: WSStubRequestSchemaCallback): WSStubRequestSchema {
    this.callback = callback;
    return this;
  }

  withResponse(res: WSStubRequestSchemaResponse): WSStubRequestSchema {
    this.response = res;
    return this;
  }

  withDefaultResponse(error?: string | null): WSStubRequestSchema {
    this.response = new WSStubResponse(this.command, undefined, error);
    return this;
  }

  match(data: any): boolean {
    if (data === null && data === undefined) {
      return false;
    }

    if (data.command === this.command) {
      for (let [name, types, required] of this.params) {
        if (data[name] !== null && data[name] !== undefined) {
          if (types.length === 0 || types.includes(data[name].constructor)) {
            continue;
          } else {
            return false;
          }
        } else {
          if (required) return false;
        }
      }

      return true;
    }

    return false;
  }
}

export function cmd(command: string) {
  return new WSStubRequestSchema(command);
}

export abstract class WebSocketServiceStubBase extends WebSocketServiceBase {
  get connectionState$(): Observable<WebSocketConnectionState> {
    return this.connectionStateSubject.asObservable();
  }

  get messages$(): Observable<any> {
    return this.messageSubject.pipe(delay(500));
  }

  private messageSubject: Subject<any>;
  private connectionStateSubject: Subject<WebSocketConnectionState>;
  private connectionState: WebSocketConnectionState = WebSocketConnectionState.Closed;
  protected commands: WSStubRequestSchema[] = [];

  constructor(config: WebSocketServiceConfig) {
    super(config);

    this.messageSubject = new Subject<any>();
    this.connectionStateSubject = new Subject<WebSocketConnectionState>();
    this.commands = this.generateListOfCommands();
  }

  protected abstract generateListOfCommands(): WSStubRequestSchema[];

  override connect(): void {
    if (this.connectionState !== WebSocketConnectionState.Closed) {
      return;
    }

    this.setConnectionState(WebSocketConnectionState.Connecting);
    of(undefined)
      .pipe(delay(500))
      .subscribe(() => this.setConnectionState(WebSocketConnectionState.Opened));
  }

  override send(data: any): void {
    if (this.connectionState === WebSocketConnectionState.Opened) {
      if (data instanceof Blob) {
        console.log('Sending BLOB (' + data.size + ' bytes)');
      } else {
        console.log('Sending data: ' + JSON.stringify(data));
      }
      this.onMessageReceived(data);
    } else {
      this.messageSubject.error('Invalid connection state: ' + this.connectionState);
    }
  }

  override disconnect(): void {
    if (this.connectionState === WebSocketConnectionState.Closed || this.connectionState === WebSocketConnectionState.Disconnecting) {
      this.connectionStateSubject.error('Service connection is closing or closed.');
    } else {
      this.setConnectionState(WebSocketConnectionState.Disconnecting);
      of(undefined)
        .pipe(delay(100))
        .subscribe(() => this.setConnectionState(WebSocketConnectionState.Closed));
    }
  }

  protected sendResponse(data: any) {
    if (data instanceof Blob) {
      console.log('Response: BLOB (' + data.size + ' bytes)');
      this.messageSubject.next(data);
    } else {
      console.log('Response: ' + JSON.stringify(this.dataToJson(data)));
      this.messageSubject.next(this.dataToJson(data));
    }
  }

  protected onBlobReceived(data: Blob) {}

  private dataToJson(data: any) {
    if (data instanceof WSStubResponse) {
      return data.toJson();
    } else {
      return data;
    }
  }

  private onMessageReceived(data: any) {
    if (data instanceof Blob) {
      this.onBlobReceived(data);
    } else {
      for (let cmd of this.commands) {
        if (cmd.match(data)) {
          if (cmd.callback) {
            cmd.callback.call(this, data);
          } else if (cmd.response) {
            if (typeof cmd.response === 'function') {
              this.sendResponse(cmd.response.call(this, data));
            } else {
              this.sendResponse(cmd.response);
            }
          }

          return;
        }
      }
    }

    // Команда не существует
    this.sendResponse({
      error: 'command not supported',
      result: false,
    });
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionStateSubject.next(state);
    }
  }
}

export const WSSTUB_GENERIC_ERROR = '[WebSocketStub] Эта команда не поддерживается. Возможно стоит обновить заглушку';
